import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

type CancellingSessionsContextValue = {
  ids: string[];
  markCancelling: (sessionId: string) => void;
  unmarkCancelling: (sessionId: string) => void;
};

const DEFAULT_VALUE: CancellingSessionsContextValue = {
  ids: [],
  markCancelling: () => {},
  unmarkCancelling: () => {},
};

const CancellingSessionsContext =
  createContext<CancellingSessionsContextValue>(DEFAULT_VALUE);

export const CancellingSessionsProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [ids, setIds] = useState<string[]>([]);

  const markCancelling = useCallback((sessionId: string) => {
    setIds((prev) => (prev.includes(sessionId) ? prev : [...prev, sessionId]));
  }, []);

  const unmarkCancelling = useCallback((sessionId: string) => {
    setIds((prev) => prev.filter((id) => id !== sessionId));
  }, []);

  const value = useMemo(
    () => ({ ids, markCancelling, unmarkCancelling }),
    [ids, markCancelling, unmarkCancelling],
  );

  return (
    <CancellingSessionsContext value={value}>
      {children}
    </CancellingSessionsContext>
  );
};

export const useCancellingSessions = (): CancellingSessionsContextValue =>
  useContext(CancellingSessionsContext);

export const useIsCancellingSession = (sessionId: string | null): boolean => {
  const { ids } = useContext(CancellingSessionsContext);
  return sessionId !== null && ids.includes(sessionId);
};
